import { Injectable } from '@angular/core';
import { StorageService } from '@core/services/storage.service';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import {
    BasketViewModel,
    OkReservation,
    ReservationLineRequest,
    ReserveInStoreRequest
} from '@models/api-orders.models';
import { filter, map, shareReplay, take, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { StoresService } from '@features/stores-overview/service/stores.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NotificationService } from '@core/services/notification/notification.service';
import { mapToReservationLines } from '@features/reserve-in-store/helper/map-to-reservation-lines';
import { mapToReservation } from '@features/reserve-in-store/helper/map-to-reservation';
import { GtmService } from '@core/services/gtm/gtm.service';
import { RelewiseTrackingService } from '@core/services/relewise/relewise-tracking.service';
import { mapToRelewiseTrackingLine } from '@features/reserve-in-store/helper/map-to-relewise-tracking-line';
import { mapToTrackReserveInStoreLines } from '@utils/gtm-tracking/utils/mapToReserveInStore';
import {
    ReserveInStore,
    ReserveInStoreUserInformation,
    ReserveProductDetail
} from '@features/reserve-in-store/reserve-in-store';

const initialReserveInStoreData: ReserveInStore = {
    storeId: '',
    storeName: '',
    reserveInStore: true,
    totalProductsCount: 0,
    totalPrice: '',
    totalPriceRaw: 0,
    vatAmount: '',
    vatAmountRaw: 0,
    totalLinesPrice: '',
    totalLinesDiscountAmount: '',
    totalLinesDiscountAmountRaw: 0,
    totalLinesPriceBeforeDiscount: '',
    totalLinesPriceBeforeDiscountRaw: 0,
    items: [],
    wasCleared: false,
    customerName: '',
    customerEmail: '',
    customerMobile: ''
};

const emailsToExcludeFromTracking = ['@kop-kande.dk'];

@UntilDestroy()
@Injectable({
    providedIn: 'root'
})
export class ReserveInStoreService {
    private readonly reserveInStoreKey = 'reserve_in_store';
    private readonly reserveInStoreBs = new BehaviorSubject<ReserveInStore>(
        this.storageService.get(this.reserveInStoreKey) ?? initialReserveInStoreData
    );

    public reserveInStore$ = this.reserveInStoreBs;

    constructor(
        private readonly relewiseTrackingService: RelewiseTrackingService,
        private readonly notificationService: NotificationService,
        private readonly storageService: StorageService,
        private readonly storesService: StoresService,
        private readonly gtmService: GtmService,
        private readonly http: HttpClient
    ) {
        this.reserveInStore$.pipe(untilDestroyed(this)).subscribe(data => {
            this.storageService.set<ReserveInStore>(this.reserveInStoreKey, data, {
                sessionOnly: true
            });
        });

        this.storesService.favoriteStore$
            .pipe(
                filter(store => !!store),
                untilDestroyed(this)
            )
            .subscribe(store =>
                this.addStoreToReservation(
                    store?.storeId?.toString() ?? '',
                    store?.storeName ?? '',
                    store?.reserveInStore || false
                )
            );
    }

    /**
     *
     * Only when the component is rendered we will get the most updated version of the
     * reservation, this will call the calculate methods
     *
     */
    public getMostUpdatedReservation(): void {
        const currentReservation = { ...this.reserveInStoreBs.value };

        this.calculateLinesReservation(currentReservation.items || [], currentReservation.storeId)
            .pipe(take(1))
            .subscribe();
    }

    public addStoreToReservation(storeId: string, storeName: string, reserveInStore: boolean): void {
        const currentReservation = { ...this.reserveInStoreBs.value };
        if (!storeId || currentReservation.storeId === storeId) {
            return;
        }

        const updatedReservation: ReserveInStore = {
            ...currentReservation,
            storeId,
            storeName,
            reserveInStore
        };

        this.reserveInStoreBs.next(updatedReservation);
        this.getMostUpdatedReservation();
    }

    public updateUserInfo(userInfo: ReserveInStoreUserInformation): void {
        const currentReservation = { ...this.reserveInStoreBs.value };
        const updatedReservation: ReserveInStore = {
            ...currentReservation,
            ...userInfo
        };
        this.reserveInStoreBs.next(updatedReservation);
    }

    public addProduct(product: ReserveProductDetail | null, quantity: number): Observable<ReserveInStore> {
        if (!product) {
            return of();
        }

        return this.updateReservation(product, quantity);
    }

    public updateProduct(product: ReserveProductDetail, quantity: number): void {
        this.updateReservation(product, quantity).pipe(take(1)).subscribe();
    }

    public removeProduct(product: ReserveProductDetail): void {
        this.updateReservation(product, 0)
            .pipe(
                map(reservation => {
                    if (reservation.items.length === 0) {
                        reservation.wasCleared = true;
                    }
                    return reservation;
                }),
                take(1)
            )
            .subscribe();
    }

    public removeAllProducts(): void {
        const { storeId, storeName, customerName, customerEmail, customerMobile } = this.reserveInStoreBs.value;

        const updatedReservation: ReserveInStore = {
            ...initialReserveInStoreData,
            items: [],
            storeId,
            storeName,
            customerName,
            customerEmail,
            customerMobile,
            wasCleared: true
        };

        this.reserveInStoreBs.next(updatedReservation);
    }

    public getProductReservationQuantity(variantNo: string): ReserveProductDetail | undefined {
        const currentReservation = { ...this.reserveInStoreBs.value };
        return currentReservation.items.find(item => item.variantNo === variantNo);
    }

    public calculateLinesReservation(
        items: ReservationLineRequest | ReserveProductDetail[],
        storeId: string
    ): Observable<ReserveInStore> {
        const currentReservation = { ...this.reserveInStoreBs.value };

        return this.calculateReservation(items, storeId).pipe(
            map(reservation => mapToReservation(reservation)),
            map(reserveInStore => ({ ...currentReservation, ...reserveInStore })),
            tap(result => {
                this.reserveInStoreBs.next(result);
            }),
            shareReplay(1)
        );
    }

    public calculateLineReservation(items: ReservationLineRequest, storeId: string): Observable<BasketViewModel> {
        return this.calculateReservation(items, storeId);
    }

    public makeReservation(
        userInformation: Pick<
            ReserveInStoreRequest,
            'customerName' | 'customerEmail' | 'customerConsent' | 'customerMobile'
        >
    ): Observable<OkReservation> {
        const { storeId, storeName, items, totalPriceRaw } = this.reserveInStoreBs.value;

        if (!storeId) {
            return throwError('Invalid Store Id');
        }

        const payload: ReserveInStoreRequest = {
            storeId: String(storeId),
            lines: mapToReservationLines(items),
            ...userInformation
        };

        return this.http.post<OkReservation>(`orders-ms/Reservation`, payload).pipe(
            tap(
                ({ orderKey }) => {
                    const excludeFromTracking = emailsToExcludeFromTracking.some(email =>
                        userInformation.customerEmail.includes(email)
                    );

                    // KOP-2860 | if an email or part of it is in the exclude list, we will not track the reservation
                    // this is to avoid tracking internal reservations (like the ones made by the store)
                    if (!excludeFromTracking) {
                        // let's set a temporary user data to be used in the tracking
                        this.gtmService.setTemporaryUserData(userInformation);
                        this.trackReserveInStore(storeName || '', totalPriceRaw, items, orderKey);
                    }

                    this.relewiseTrackingService.trackBasket('Order', orderKey, {
                        lineItems: mapToRelewiseTrackingLine(items),
                        totalAmount: Number(totalPriceRaw),
                        channel: 'ReservedInStore'
                    });
                },
                errorRes => {
                    // We can get error as string, as string[] and as message, so we must do some mapping
                    const errorText = Array.isArray(errorRes.error)
                        ? errorRes.error.join('\n')
                        : errorRes.error || errorRes.message || '';

                    this.notificationService.createNotification({
                        ...(errorText ? { messageText: errorText } : {}),
                        fadeTime: Array.isArray(errorRes.error) ? errorRes.error.length * 6000 : 8000
                    });
                }
            ),
            take(1),
            untilDestroyed(this)
        );
    }

    public clearReservation(hasReservation: boolean): void {
        const currentReservation = { ...this.reserveInStoreBs.value };
        const cleared = { ...initialReserveInStoreData, items: [] };

        if (
            (hasReservation && currentReservation.items.length) ||
            (!hasReservation && !currentReservation.items.length)
        ) {
            this.reserveInStoreBs.next({ ...cleared, wasCleared: false });
        }
    }

    private calculateReservation(
        items: ReservationLineRequest | ReserveProductDetail[],
        storeId: string
    ): Observable<BasketViewModel> {
        const reserveLines: ReservationLineRequest[] = Array.isArray(items) ? mapToReservationLines(items) : [items];

        return this.http
            .post<BasketViewModel>(`orders-ms/Reservation/Calculate`, {
                storeId: storeId,
                lines: reserveLines
            })
            .pipe(
                map(reservation => {
                    if (reservation.errorMessages) {
                        throwError({ error: reservation.errorMessages });
                    }
                    return reservation;
                })
            );
    }

    private updateReservation(product: ReserveProductDetail, quantity: number): Observable<ReserveInStore> {
        const currentReservation = { ...this.reserveInStoreBs.value };
        const lines: ReserveProductDetail[] = currentReservation.items;
        const lineIndex = lines.findIndex(line => line.variantNo === product.variantNo);

        if (lineIndex !== -1) {
            if (quantity !== 0) {
                let newQuantity = product.quantity + quantity;
                if (newQuantity >= product.stockQuantity) {
                    newQuantity = product.stockQuantity;
                }

                lines[lineIndex].quantity = newQuantity;
            } else {
                lines.splice(lineIndex, 1);
            }
        } else {
            lines.push({ ...product, quantity });
        }

        return this.calculateLinesReservation(lines, currentReservation.storeId);
    }

    private trackReserveInStore(
        storeName: string,
        total: number,
        lines: ReserveProductDetail[],
        transactionId: string
    ) {
        if (!storeName) {
            return;
        }

        // yes, we are tracking two, because one is for Impact Extend and the another is for 3rd Party Agency
        this.gtmService.trackReserveInStoreReservation(lines, storeName);
        this.gtmService.trackingEvents.trackReserveInStore(
            storeName,
            transactionId,
            total,
            mapToTrackReserveInStoreLines(lines)
        );
    }
}
